import {
  formatHours,
  type BulkApproveFailureReason,
  type OutstandingApprovalItem,
} from '@time-tracking/shared';

import { AlertTriangleIcon } from '../icons/AlertTriangleIcon.js';
import { CheckIcon } from '../icons/CheckIcon.js';
import { ChevronDownIcon } from '../icons/ChevronDownIcon.js';
import { ChevronRightIcon } from '../icons/ChevronRightIcon.js';
import { ClockIcon } from '../icons/ClockIcon.js';
import { UnlinkIcon } from '../icons/UnlinkIcon.js';
import { UserIcon } from '../icons/UserIcon.js';
import { UserPlusIcon } from '../icons/UserPlusIcon.js';
import { StatusPill } from '../StatusPill.js';
import { BULK_FAILURE_LABELS } from './bulkApprove.js';
import { hoursBucket, hoursBucketClass } from '../../utils/hours.js';
import { formatWeekRange } from '../../utils/weekDates.js';
import {
  describeGroup,
  isBulkApprovable,
  groupItems,
  groupSeverity,
  type ActionGroup,
  type GroupBy,
} from './needsActionGrouping.js';

/** "Mon, Jun 1" — the due-date form, anchored at UTC noon like every other date here. */
// Hoisted: AgeTag calls this for most rows, and the table re-renders on every
// selection change, group toggle and background refetch — rebuilding the formatter
// per row per render is up to 200 needless constructions each time.
const DUE_DAY_FORMAT = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  timeZone: 'America/Chicago',
});

function fmtDueDay(dueAt: string): string {
  return DUE_DAY_FORMAT.format(new Date(dueAt));
}

/**
 * Lateness chip.
 *
 * Severity is carried by the day count and the glyph as well as the tone, never
 * by colour alone. The tones are the Tangerine caution family rather than the
 * prototype's red: Brand Book V2.1 rules red off-brand, and tokens.css aliases
 * --color-destructive to Bluebonnet, so the codebase refuses red outright.
 */
function AgeTag({ item }: { item: OutstandingApprovalItem }) {
  if (item.kind === 'approve') {
    // A submitted sheet nobody has acted on. Counted from the deadline rather
    // than from submission, so it lines up with everything else on the row.
    if (item.daysPastDue < 2) return null;
    const tone = item.daysPastDue >= 7 ? 'act-age--alert' : 'act-age--warn';
    return (
      <span className={`act-age ${tone}`} title={`Waiting ${item.daysPastDue} days`}>
        <ClockIcon />
        {`waiting ${item.daysPastDue}d`}
      </span>
    );
  }
  if (item.daysPastDue > 0) {
    const tone =
      item.daysPastDue >= 14
        ? 'act-age--alert'
        : item.daysPastDue >= 7
          ? 'act-age--warn'
          : 'act-age--soft';
    return (
      <span className={`act-age ${tone}`} title={`Due ${fmtDueDay(item.dueAt)}`}>
        <AlertTriangleIcon />
        {`${item.daysPastDue}d past due`}
      </span>
    );
  }
  return <span className="act-age act-age--open">due {fmtDueDay(item.dueAt)}</span>;
}

/** Why a row has no action. Reminders are not a product feature; name who it waits on. */
function waitingLabel(item: OutstandingApprovalItem): string {
  const first = item.name.split(' ')[0] ?? item.name;
  if (item.kind === 'waiting') return `Awaiting ${first}'s resubmit`;
  return item.teamId === null ? 'No approver of record' : `Awaiting ${first}'s submission`;
}

interface ActionTableProps {
  items: readonly OutstandingApprovalItem[];
  groupBy: GroupBy;
  selectedKeys: Set<string>;
  onSelectionChange: (next: Set<string>) => void;
  collapsedGroups: Set<string>;
  onToggleGroup: (key: string) => void;
  onOpenItem: (item: OutstandingApprovalItem) => void;
  onApprove: (item: OutstandingApprovalItem) => void;
  onReturn: (item: OutstandingApprovalItem) => void;
  onApproveGroup: (group: ActionGroup) => void;
  onAssignTeam: (item: OutstandingApprovalItem, anchorRect: DOMRect) => void;
  onOpenWeek?: (weekStartDate: string) => void;
  /** Lets a team group say "You are the lead" rather than naming the viewer back. */
  currentUserId?: string | null;
  /** Row key → why the last bulk approve refused it, shown against that row (R23). */
  failureReasons?: Record<string, BulkApproveFailureReason>;
  /**
   * Rows whose request is in flight. Their own actions are disabled until it settles —
   * a second click would otherwise send a second POST under a different idempotency key,
   * and the viewer would get "Approved" and "Failed to approve" for one action.
   */
  inFlightKeys?: ReadonlySet<string>;
}

export function ActionTable({
  items,
  currentUserId = null,
  groupBy,
  selectedKeys,
  onSelectionChange,
  collapsedGroups,
  onToggleGroup,
  onOpenItem,
  onApprove,
  onReturn,
  onApproveGroup,
  onAssignTeam,
  onOpenWeek,
  failureReasons = {},
  inFlightKeys,
}: ActionTableProps) {
  const groups = groupItems(items, groupBy);
  // Select-all spans the rows actually returned, never the uncapped totals: a
  // button labelled from a total it cannot reach would approve fewer sheets than
  // it claimed. Own sheet excluded — self-approval stays a deliberate single act.
  const selectableKeys = items.filter(isBulkApprovable).map((i) => i.key);
  const allSelected = selectableKeys.length > 0 && selectableKeys.every((k) => selectedKeys.has(k));
  const someSelected = selectableKeys.some((k) => selectedKeys.has(k));

  return (
    <div className="act-table-wrap">
      <table className="act-table">
        <thead>
          <tr>
            <th scope="col" className="act-col-cb">
              <input
                type="checkbox"
                aria-label="Select every approvable sheet"
                checked={allSelected}
                // Nothing here can be bulk-approved — every row is the viewer's own, an
                // AOBO row, or not submitted. Activating it cannot change its own state
                // or select anything, and a control that answers a keypress with nothing
                // is worse than one that is plainly unavailable.
                disabled={selectableKeys.length === 0}
                // React has no indeterminate prop; the DOM property is the only
                // way to get the mixed state, and it is what screen readers read.
                ref={(el) => {
                  if (el) el.indeterminate = someSelected && !allSelected;
                }}
                onChange={(e) =>
                  onSelectionChange(e.target.checked ? new Set(selectableKeys) : new Set())
                }
              />
            </th>
            <th scope="col">{groupBy === 'person' ? 'Week' : 'Person'}</th>
            <th scope="col" className="act-col-hours">
              Hours
            </th>
            <th scope="col">Status</th>
            <th scope="col">Action</th>
          </tr>
        </thead>

        {groups.flatMap((group) => {
          const collapsed = collapsedGroups.has(group.key);
          const bodyId = `act-group-body-${group.key}`;
          const severity = groupSeverity(group.worstDaysPastDue);
          const first = group.items[0];
          const description = describeGroup(
            group,
            groupBy,
            (weekStart) => formatWeekRange(weekStart, first?.weekEndDate ?? weekStart),
            fmtDueDay,
            currentUserId,
          );

          return [
            <tbody key={`${group.key}-head`} className="act-group">
              <tr
                className={`act-group-head${severity === 'none' ? '' : ` act-group-head--${severity}`}`}
              >
                {/* No `scope`: each group is two sibling <tbody>s, so `colgroup` would
                    offer this full-width cell as the column header for rows in OTHER
                    groups under table-navigation commands, and `rowgroup` only reaches
                    cells in the same rowgroup — which the data rows are not in. */}
                <th colSpan={5}>
                  <div className="act-group-head__inner">
                    {/* A real button, not a clickable row: for a <tr> neither
                        axe-core nor jsx-a11y reports the missing semantics. The heading
                        WRAPS the button rather than sitting inside it — <h3> is not
                        permitted content for <button> — so the group structure is
                        reachable by heading navigation, which is how a screen-reader
                        user moves through a long backlog. */}
                    <h3 className="act-group-head__heading">
                      <button
                        type="button"
                        className="act-group-head__toggle"
                        aria-expanded={!collapsed}
                        aria-controls={bodyId}
                        onClick={() => onToggleGroup(group.key)}
                      >
                        {collapsed ? <ChevronRightIcon /> : <ChevronDownIcon />}
                        {description.initials !== null && (
                          <span className="act-avatar" aria-hidden="true">
                            {description.initials}
                          </span>
                        )}
                        <span className="act-group-head__block">
                          <span className="act-group-head__title">
                            {description.title}
                            {description.tag !== null && (
                              <span className="act-tag act-tag--noteam act-group-head__tag">
                                {description.tag}
                              </span>
                            )}
                          </span>
                          <span className="act-group-head__meta">{description.meta}</span>
                        </span>
                      </button>
                    </h3>

                    <span className="act-group-head__rollup">
                      {group.counts.approve > 0 && (
                        <span className="rollup-pill rollup-pill--blue">
                          {group.counts.approve} to approve
                        </span>
                      )}
                      {group.counts.nudge > 0 && (
                        <span className="rollup-pill rollup-pill--warn">
                          {group.counts.nudge} not submitted
                        </span>
                      )}
                      {group.counts.waiting > 0 && (
                        <span className="rollup-pill rollup-pill--soft">
                          {group.counts.waiting} awaiting fix
                        </span>
                      )}
                      {severity !== 'none' && (
                        <span className="rollup-pill rollup-pill--late">
                          <AlertTriangleIcon />
                          {group.worstDaysPastDue}d past due
                        </span>
                      )}
                    </span>

                    <span className="act-group-head__actions">
                      {groupBy === 'week' && onOpenWeek && !first?.isCurrentWeek && (
                        <button
                          type="button"
                          className="btn btn--sm btn--light"
                          onClick={() => onOpenWeek(group.key)}
                        >
                          Open week
                        </button>
                      )}
                      {group.approvableCount > 1 && (
                        <button
                          type="button"
                          className="btn btn--sm btn--light"
                          onClick={() => onApproveGroup(group)}
                        >
                          <CheckIcon /> Approve {group.approvableCount}
                        </button>
                      )}
                    </span>
                  </div>
                </th>
              </tr>
            </tbody>,
            <tbody key={bodyId} id={bodyId} hidden={collapsed}>
              {group.items.map((item) => {
                const selected = selectedKeys.has(item.key);
                const canSelect = isBulkApprovable(item);
                // Gated on the selection, which is what makes it self-clearing. R23 keeps a
                // failed row selected, and the view clears the selection on every filter,
                // search, group-by and scope change — so the label cannot outlive the run
                // that produced it. Keyed on `${userId}@${weekStartDate}`, which is stable
                // across all of those, it otherwise persisted indefinitely: a "Reporting
                // period closed" badge still sitting on a row three filters later.
                const failure = selected ? failureReasons[item.key] : undefined;
                const rowBusy = inFlightKeys?.has(item.key) === true;
                // 'returned' included: the row prints its real totalMinutes a few
                // lines down, so excluding it here bucketed real hours as if they were
                // zero and coloured the figure against the wrong threshold.
                const showsHours =
                  item.status === 'submitted' ||
                  item.status === 'draft' ||
                  item.status === 'returned';
                const bucket = hoursBucket(showsHours ? item.totalMinutes : 0, item.targetMinutes);
                const label =
                  groupBy === 'person'
                    ? formatWeekRange(item.weekStartDate, item.weekEndDate)
                    : item.name;

                return (
                  <tr
                    key={item.key}
                    // Focus management reads this when a row is approved away —
                    // see nextFocusTarget in bulkApprove.ts.
                    data-row-key={item.key}
                    className={`act-row${selected ? ' act-row--selected' : ''}${
                      item.teamId === null ? ' act-row--unassigned' : ''
                    }${groupBy === 'person' ? ' act-row--week-label' : ''}`}
                    onClick={() => onOpenItem(item)}
                  >
                    <td className="act-col-cb" onClick={(e) => e.stopPropagation()}>
                      {canSelect ? (
                        <input
                          type="checkbox"
                          // Names the person AND the week: the same person appears
                          // once per outstanding week, so the name alone is ambiguous.
                          aria-label={`Select ${item.name}, ${formatWeekRange(
                            item.weekStartDate,
                            item.weekEndDate,
                          )}`}
                          checked={selected}
                          onChange={(e) => {
                            const next = new Set(selectedKeys);
                            if (e.target.checked) next.add(item.key);
                            else next.delete(item.key);
                            onSelectionChange(next);
                          }}
                        />
                      ) : (
                        // Keeps the column width so the grid never shifts between
                        // actionable and non-actionable rows.
                        <span className="act-cb-placeholder" aria-hidden="true" />
                      )}
                    </td>

                    <td>
                      {/* Under Week and Team grouping the row's subject is a person, so
                          it carries the avatar the group header carries in Person mode. */}
                      {groupBy !== 'person' && (
                        <span className="act-avatar act-avatar--row" aria-hidden="true">
                          {item.initials}
                        </span>
                      )}
                      <button
                        type="button"
                        className="row-name-btn act-row__name"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenItem(item);
                        }}
                      >
                        {label}
                      </button>
                      <span className="act-row__tags">
                        {item.isCurrentWeek && (
                          <span className="act-tag act-tag--current">current</span>
                        )}
                        {item.isSelf && (
                          <span className="act-tag act-tag--self">
                            <UserIcon /> your timesheet
                          </span>
                        )}
                        {item.teamId === null && (
                          <span className="act-tag act-tag--noteam">
                            <UnlinkIcon /> no team
                          </span>
                        )}
                        {!item.activeFlag && (
                          <span className="act-tag act-tag--inactive">inactive</span>
                        )}
                        <AgeTag item={item} />
                      </span>
                      <span className="act-row__sub">
                        {groupBy === 'person'
                          ? item.status === 'missing'
                            ? 'no submission'
                            : item.status === 'draft'
                              ? `${formatHours(item.totalMinutes)}h saved, not submitted`
                              : item.status === 'returned'
                                ? 'returned for fix, not resubmitted'
                                : 'submitted'
                          : // Drop whichever field the group header already carries —
                            // otherwise every row under "Jun 29 – Jul 5" repeats that
                            // range, and every row of the Cloud group repeats "Cloud".
                            [
                              item.utEid,
                              groupBy === 'week'
                                ? null
                                : formatWeekRange(item.weekStartDate, item.weekEndDate),
                              groupBy === 'team' ? null : item.teamName,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                      </span>
                    </td>

                    <td className="act-col-hours">
                      <span className={`act-hours ${hoursBucketClass(bucket)}`}>
                        {item.status === 'missing' ? (
                          '—'
                        ) : (
                          <>
                            {formatHours(item.totalMinutes)}
                            <span className="act-hours__unit">h</span>
                          </>
                        )}
                      </span>
                      <span className="act-row__target">
                        target {formatHours(item.targetMinutes)}h
                      </span>
                    </td>

                    <td>
                      <StatusPill status={item.status} />
                      {item.anomalies.length > 0 && (
                        <span
                          className="act-anomaly"
                          title={item.anomalies.map((a) => a.label).join(' · ')}
                        >
                          <AlertTriangleIcon /> {item.anomalies[0]?.label}
                        </span>
                      )}
                    </td>

                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="act-row__actions">
                        {/* Why the last bulk approve refused this row (R23). Text, not a
                            tone alone — colour is never the sole indicator of state — and
                            placed on the row because that is where the remedy is: a
                            closed period is final, a permission problem needs an
                            administrator, a stale one just needs sending again. */}
                        {/* Not a live region. It was `role="status"`, mounted only when a
                            failure existed — so it appeared already populated, which NVDA
                            and VoiceOver frequently do not announce, and a row failing the
                            same way twice produced no text change and so no announcement
                            at all. The bulk outcome is already spoken once, assertively,
                            by the toast; this is the visible detail a reader meets when
                            they reach the row, not a second competing announcement. */}
                        {failure !== undefined && (
                          <span className="act-row__failure">
                            <AlertTriangleIcon />
                            {BULK_FAILURE_LABELS[failure]}
                          </span>
                        )}
                        {item.kind === 'approve' && !item.aobo && (
                          <>
                            <button
                              type="button"
                              className="btn btn--sm btn--secondary"
                              disabled={rowBusy}
                              onClick={() => onReturn(item)}
                              aria-label={`Return ${item.name}'s timesheet for correction`}
                            >
                              Return
                            </button>
                            <button
                              type="button"
                              className="btn btn--sm primary"
                              disabled={rowBusy}
                              onClick={() => onApprove(item)}
                              aria-label={`Approve ${item.name}'s timesheet`}
                            >
                              <CheckIcon /> {rowBusy ? 'Approving…' : 'Approve'}
                            </button>
                          </>
                        )}
                        {item.kind === 'approve' && item.aobo && (
                          <button
                            type="button"
                            className="btn btn--sm btn--review"
                            onClick={() => onOpenItem(item)}
                          >
                            Review <ChevronRightIcon />
                          </button>
                        )}
                        {item.kind !== 'approve' && (
                          <>
                            {/* No Remind control, deliberately: automated reminders
                                are deferred (U-08) and a button that does nothing
                                is worse than none. Name who the row waits on. */}
                            <span className="act-row__waiting">{waitingLabel(item)}</span>
                            {/* Nudge only, not every non-approve row. R6 gives waiting
                                rows no action button and R7 scopes the Assign exception
                                to a teamless `nudge` — it is a structural fix for a
                                person with no approver of record, and a returned sheet
                                has an approver already and is waiting on its author to
                                resubmit. Teamless-and-returned is reachable only if
                                someone loses their team after a return; that person is
                                still assignable from the Users admin pane. */}
                            {item.teamId === null && item.kind === 'nudge' && (
                              <button
                                type="button"
                                className="btn btn--sm btn--light"
                                onClick={(e) =>
                                  onAssignTeam(item, e.currentTarget.getBoundingClientRect())
                                }
                              >
                                <UserPlusIcon /> Assign team
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>,
          ];
        })}
      </table>
    </div>
  );
}
